import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getUnreadNotificationsCount } from '../../shared/api/notifications.api'
import { useAuthStore } from '../auth/authStore'

const BASE_TITLE = 'micro·hub — Microelectronics CRM'
const BASE_FAVICON = '/favicon.svg'
const BADGE_FAVICON_ID = 'crm-favicon-badge'

/**
 * Когда пришли новые уведомления и вкладка не активна — показываем счётчик
 * в title и рисуем красный кружок с цифрой поверх favicon. При возврате на
 * вкладку (visibilitychange → visible) восстанавливаем оригинал.
 *
 * Подписан на тот же `getUnreadNotificationsCount`, что и колокольчик —
 * staleTime 30s; обновится при WS-событии notifications.unread_count.updated
 * через invalidate.
 */
export function useTabBadge() {
  const role = useAuthStore((s) => s.user?.role)
  const isLogged = Boolean(role)

  const { data } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadNotificationsCount,
    enabled: isLogged,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const unread = data?.unreadCount ?? 0
  const lastAppliedRef = useRef<number>(-1)

  useEffect(() => {
    if (!isLogged) {
      restoreTitle()
      restoreFavicon()
      return
    }
    applyTitle(unread)
    applyFavicon(unread).catch(() => {
      /* Canvas могут заблокировать на старых браузерах — просто title останется */
    })
    lastAppliedRef.current = unread
  }, [unread, isLogged])

  // При возврате на вкладку сразу убираем badge — пользователь увидел.
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) {
        restoreTitle()
        restoreFavicon()
      } else if (lastAppliedRef.current > 0) {
        // вернулся в фон — восстановить badge
        applyTitle(lastAppliedRef.current)
        void applyFavicon(lastAppliedRef.current)
      }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])
}

function applyTitle(count: number): void {
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${BASE_TITLE}` : BASE_TITLE
}

function restoreTitle(): void {
  document.title = BASE_TITLE
}

async function applyFavicon(count: number): Promise<void> {
  if (count <= 0) {
    restoreFavicon()
    return
  }
  // Загружаем оригинал как Image, рисуем поверх красный кружок с цифрой.
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = BASE_FAVICON
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('favicon load failed'))
  })

  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    restoreFavicon()
    return
  }
  ctx.drawImage(img, 0, 0, size, size)

  // Красный кружок справа сверху — стандартный паттерн badge.
  const text = count > 99 ? '99+' : String(count)
  const cx = size - 18
  const cy = 18
  const r = text.length > 2 ? 18 : 14
  ctx.fillStyle = '#ef4444'
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#fff'
  ctx.font = `bold ${text.length > 2 ? 14 : 18}px system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, cx, cy + 1)

  swapFavicon(canvas.toDataURL('image/png'))
}

function restoreFavicon(): void {
  const badge = document.getElementById(BADGE_FAVICON_ID)
  badge?.remove()
  // Оригинальный <link rel="icon"> остаётся в head — браузер сам подхватит.
}

function swapFavicon(dataUrl: string): void {
  let link = document.getElementById(BADGE_FAVICON_ID) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.id = BADGE_FAVICON_ID
    link.rel = 'icon'
    link.type = 'image/png'
    document.head.appendChild(link)
  }
  link.href = dataUrl
}
